'use client';

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { LoadingState } from '@org/design-system';
import type { AuthConfig } from '../types/app-config';
import { useRuntimeAuth } from './auth-context';
import { getLoginPath, isAuthEnabled, isExternalAuthUrl } from './auth-paths';

export interface AuthGuardProps {
  children: ReactNode;
  auth?: AuthConfig;
}

export function AuthGuard({ children, auth }: AuthGuardProps) {
  const location = useLocation();
  const { isAuthenticated, isLoading } = useRuntimeAuth();

  if (!isAuthEnabled(auth)) {
    return <>{children}</>;
  }

  if (isLoading) {
    return <LoadingState message="Checking authentication..." />;
  }

  if (isAuthenticated) {
    return <>{children}</>;
  }

  const loginPath = getLoginPath(auth);
  if (isExternalAuthUrl(loginPath)) {
    window.location.assign(loginPath);
    return null;
  }

  return (
    <Navigate
      to={loginPath}
      replace
      state={{ from: `${location.pathname}${location.search}${location.hash}` }}
    />
  );
}
